// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Related shipments tab on the location detail page. Lists shipments whose
// origin or destination matches the current location (or any of its zones
// when viewing a site).
//
// The shipment table component is single-shipment-type by design: each row
// branches on the shipment type for references, dates, and actions, and the
// export URL uses the table's filter shipmentType. We render exactly one
// table at a time and use an Outbound / Inbound filter (mirroring the
// project tickets and files-and-links pattern) to switch between
// directions, with each direction tracking its own page in URL state.
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { LocationErrorBoundary } from '@/location/components/location-error-boundary';
import { useLocation } from '@/location/hooks/use-location';
import { ShipmentTable } from '@/shipment/components/shipment-table';
import { ShipmentTableSkeleton } from '@/shipment/components/shipment-table-skeleton';
import { Box, Tab, Tabs } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
  outboundPage: z.number().optional(),
  inboundPage: z.number().optional(),
});

const PAGE_SIZE = 20;

const TAB_OUTBOUND = 0;
const TAB_INBOUND = 1;

export const Route = createFileRoute(
  '/_app/locations/$locationKey/related-shipments'
)({
  component: RelatedShipmentsRoute,
  validateSearch: searchSchema,
});

function RelatedShipmentsRoute() {
  const { locationKey } = Route.useParams();

  return (
    <LocationErrorBoundary>
      <RelatedShipmentsContent locationKey={locationKey} />
    </LocationErrorBoundary>
  );
}

interface RelatedShipmentsContentProps {
  locationKey: string;
}

function RelatedShipmentsContent(props: RelatedShipmentsContentProps) {
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();
  const { location } = useLocation(props.locationKey);
  const [selectedTab, setSelectedTab] = useState<number>(TAB_OUTBOUND);

  // Match against the location and, when the location is a site, also any of
  // its zones. This keeps behavior consistent with the related assets tab.
  const locationIds = [location.id, ...location.zones.map((z) => z.id)];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleChangeOutboundPage = (page: number) => {
    navigate({ search: { ...searchParameters, outboundPage: page } });
  };
  const handleChangeInboundPage = (page: number) => {
    navigate({ search: { ...searchParameters, inboundPage: page } });
  };

  return (
    <HardfinTabCardWrapper title="Related shipments">
      <Box
        sx={{
          display: 'flex',
          paddingLeft: 1,
          flexDirection: 'row',
          '& .Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiTab-root': {
            px: 2,
          },
          '& .MuiTab-root.MuiButtonBase-root': {
            ml: 0,
          },
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          aria-label="Related shipment filters"
        >
          <Tab label="Outbound" />
          <Tab label="Inbound" />
        </Tabs>
      </Box>

      {selectedTab === TAB_OUTBOUND && (
        <Suspense fallback={<ShipmentTableSkeleton columns={4} />}>
          <ShipmentTable
            onPageChange={handleChangeOutboundPage}
            filter={{
              locationIds,
              shipmentType: 'outbound',
              pageSize: PAGE_SIZE,
              pageNumber: searchParameters.outboundPage ?? 1,
            }}
          />
        </Suspense>
      )}
      {selectedTab === TAB_INBOUND && (
        <Suspense fallback={<ShipmentTableSkeleton columns={4} />}>
          <ShipmentTable
            onPageChange={handleChangeInboundPage}
            filter={{
              locationIds,
              shipmentType: 'inbound',
              pageSize: PAGE_SIZE,
              pageNumber: searchParameters.inboundPage ?? 1,
            }}
          />
        </Suspense>
      )}
    </HardfinTabCardWrapper>
  );
}
